import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Users, Zap, Wind, Shield, ChevronRight, Star, X, MessageCircle, Menu } from 'lucide-react';


const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container navbar-container">
        <div className="logo">
          <img src="https://user9730.na.imgto.link/public/20260428/whatsapp-image-2026-04-27-at-2-29-23-pm-removebg-preview.avif" alt="Logo" />
        </div>
        <div className="nav-links desktop-only">
          <a href="#home">Home</a>
          <a href="#services">Services</a>
          <a href="#rentals">Rentals</a>
          <a href="#reviews">Reviews</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="nav-actions">
          <a href="https://wa.me/917977584016" target="_blank" rel="noreferrer" className="btn-primary desktop-only" style={{ padding: '0.6rem 1.5rem', fontSize: '0.75rem' }}>WhatsApp Us</a>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <a href="#home" onClick={() => setMobileMenuOpen(false)}>Home</a>
            <a href="#services" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#rentals" onClick={() => setMobileMenuOpen(false)}>Rentals</a>
            <a href="#reviews" onClick={() => setMobileMenuOpen(false)}>Reviews</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <a href="https://wa.me/917977584016" target="_blank" rel="noreferrer" className="btn-primary" style={{ textAlign: 'center', marginTop: '1rem' }} onClick={() => setMobileMenuOpen(false)}>WhatsApp Us</a>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const App = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 300]);
  const opacity1 = useTransform(scrollY, [0, 500], [1, 0]);

  const [showInstantBooking, setShowInstantBooking] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  // WHATSAPP REDIRECTION LOGIC
  /*
  const sendToWhatsApp = (e, type) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    let message = `*New Request: ${type}*\n\n`;

    for (const [key, value] of Object.entries(data)) {
      message += `*${key}:* ${value}\n`;
    }

    const phone = "917977584016";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setActiveModal(null);
    setShowInstantBooking(false);
  };
  */

  const sendToWhatsApp = (e, type) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    let message = "";


    const formatDateTime = (value) => {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    if (type === "airport booking") {

      message = `✈️ *VEDANT TRAVELS - AIRPORT TRANSFER REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Customer Name:* ${data["Name"]}\n`;

      if (data["Airport"])
        message += `🛫 *Airport / Terminal:* ${data["Airport"]}\n`;

      if (data["Address"])
        message += `📍 *Pickup / Drop Address:* ${data["Address"]}\n`;

      if (data["Date and Time"])
        message += `🗓️ *Travel Schedule:* ${formatDateTime(data["Date and Time"])}\n`;

      if (data["Passengers"])
        message += `👥 *Passengers:* ${data["Passengers"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `🚘 Kindly confirm vehicle availability and fare details.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }

    if (type === "Instant Booking") {
      message = `🚘 *VEDANT TRAVELS - NEW BOOKING REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      message += `📌 *Service Type:* ${type}\n`;

      if (data["Name"]) message += `👤 *Customer Name:* ${data["Name"]}\n`;
      if (data["Pick-up"]) message += `📍 *Pickup Location:* ${data["Pick-up"]}\n`;
      if (data["Drop-off"]) message += `🏁 *Drop Location:* ${data["Drop-off"]}\n`;
      if (data["Date and Time"]) message += `🗓️ *Date & Time:* ${formatDateTime(data["Date and Time"])}\n`;
      if (data["Phone"]) message += `📞 *Customer Phone:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `✨ Please confirm availability and fare.\n`;
      message += `Thank you.`;
    }

    if (type === "railway booking") {

      message = `🚆 *VEDANT TRAVELS - RAILWAY TRANSFER REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Customer Name:* ${data["Name"]}\n`;

      if (data["Station"])
        message += `🚉 *Railway Station:* ${data["Station"]}\n`;

      if (data["Address"])
        message += `📍 *Pickup / Drop Address:* ${data["Address"]}\n`;

      if (data["Date and Time"])
        message += `🗓️ *Travel Schedule:* ${formatDateTime(data["Date and Time"])}\n`;

      if (data["Passengers"])
        message += `👥 *Passengers:* ${data["Passengers"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `🚘 Kindly confirm vehicle availability and fare details.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }
    if (type === "sightseeing booking") {

      message = `🌍 *VEDANT TRAVELS - LOCAL SIGHTSEEING REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Customer Name:* ${data["Name"]}\n`;

      if (data["District"])
        message += `📍 *Destination / Region:* ${data["District"]}\n`;

      if (data["Days"])
        message += `🛎️ *Trip Duration:* ${data["Days"]} Day(s)\n`;

      if (data["Date and Time"])
        message += `🗓️ *Travel Schedule:* ${formatDateTime(data["Date and Time"])}\n`;

      if (data["Passengers"])
        message += `👥 *Passengers:* ${data["Passengers"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `🚘 Kindly share the best package and fare details.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }

    if (type === "business booking") {

      message = `💼 *VEDANT TRAVELS - BUSINESS TRAVEL REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Client Name:* ${data["Name"]}\n`;

      if (data["Pick-up"])
        message += `📍 *Pickup Location:* ${data["Pick-up"]}\n`;

      if (data["Destination"])
        message += `🏢 *Meeting Destination:* ${data["Destination"]}\n`;

      if (data["Duration"])
        message += `⏳ *Travel Duration Package:* ${data["Duration"]}\n`;

      if (data["Date and Time"])
        message += `🗓️ *Schedule:* ${formatDateTime(data["Date and Time"])}\n`;

      if (data["Passengers"])
        message += `👥 *Passengers:* ${data["Passengers"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `🚘 Kindly confirm executive vehicle availability and pricing.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }

    if (type === "rental_inquiry booking") {

      message = `🚘 *VEDANT TRAVELS - PREMIUM CAR RENTAL INQUIRY*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Customer Name:* ${data["Name"]}\n`;

      if (data["Vehicle"])
        message += `🚗 *Selected Vehicle:* ${data["Vehicle"]}\n`;

      if (data["Start Date"])
        message += `📅 *Rental Start Date:* ${data["Start Date"]}\n`;

      if (data["End Date"])
        message += `📅 *Rental End Date:* ${data["End Date"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `✨ Kindly share vehicle availability and rental pricing details.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }

    if (type === "rental_ertiga booking") {

      message = `🚘 *VEDANT TRAVELS - ERTIGA RESERVATION REQUEST*\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;

      if (data["Name"])
        message += `👤 *Customer Name:* ${data["Name"]}\n`;

      if (data["Vehicle"])
        message += `🚗 *Vehicle:* ${data["Vehicle"]}\n`;

      if (data["Start Date"])
        message += `📅 *Rental Start Date:* ${data["Start Date"]}\n`;

      if (data["End Date"])
        message += `📅 *Rental End Date:* ${data["End Date"]}\n`;

      if (data["Passengers"])
        message += `👥 *Passengers:* ${data["Passengers"]}\n`;

      if (data["Phone"])
        message += `📞 *Contact Number:* ${data["Phone"]}\n`;

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `✨ Kindly confirm Ertiga availability and rental pricing.\n`;
      message += `Thank you for choosing Vedant Travels.`;
    }

    const phone = "917977584016";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank");
    setActiveModal(null);
    setShowInstantBooking(false);
  };

  const servicesData = [
    {
      id: 'airport',
      title: 'Airport Transfer',
      desc: 'Seamless transfers to and from Mumbai and Navi Mumbai airports.',
      img: 'https://moodiedavittreport.com/wp-content/uploads/2023/07/CSMIA-Diwali-Decor-T2_Pic-2-1.jpg'
    },
    {
      id: 'railway',
      title: 'Railway Transfer',
      desc: 'Reliable pick-up and drop-off at all major railway stations.',
      img: 'https://www.andbeyond.com/wp-content/uploads/sites/5/Chhatrapati-Shivaji-Terminus-railway-station-mumbai.jpg'
    },
    {
      id: 'sightseeing',
      title: 'Local Sightseeing',
      desc: 'Explore the hidden gems and popular sites across Maharashtra districts.',
      img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?q=80&w=2070&auto=format&fit=crop'
    },
    {
      id: 'business',
      title: 'Business Meetings',
      desc: 'Executive travel services tailored for corporate professionals.',
      img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop'
    }
  ];

  const testimonials = [
    { name: "Rajesh K.", text: "Absolutely phenomenal service. The vehicle was pristine, and the chauffeur was exceptionally punctual and polite.", rating: 5 },
    { name: "Priya S.", text: "Used Vedant Travels for an airport drop from Palghar. Very smooth booking process and a super comfortable ride. Highly recommended!", rating: 5 },
    { name: "Amit D.", text: "We booked a 5-day sightseeing tour to Ganpatipule. Our driver knew all the best spots and navigated the traffic perfectly.", rating: 5 }
  ];

  return (
    <>

      <Helmet>
        <title>Vedant Travels | Car Rental & Chauffeur Services in Palghar</title>

        <meta
          name="description"
          content="Vedant Travels offers premium car rental, chauffeur-driven rides, airport transfers, railway transfers, local sightseeing, and business travel services across Palghar, Mumbai, Navi Mumbai, and Maharashtra."
        />

        <meta
          name="keywords"
          content="Vedant Travels, car rental Palghar, chauffeur service Maharashtra, airport transfer Mumbai, railway transfer Mumbai, local sightseeing Maharashtra, Ertiga rental Palghar, travel agency Palghar"
        />

        <meta name="author" content="Vedant Travels" />

        <meta property="og:title" content="Vedant Travels | Premium Car Rental & Chauffeur Services" />
        <meta
          property="og:description"
          content="Book reliable car rentals, airport transfers, railway transfers, business travel, and sightseeing services with Vedant Travels."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantgo.in" />
        <meta property="og:image" content="https://vedantgo.in/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vedant Travels | Car Rental & Chauffeur Services" />
        <meta
          name="twitter:description"
          content="Premium chauffeur-driven car rental and travel services across Maharashtra."
        />
        <meta name="twitter:image" content="https://vedantgo.in/og-image.jpg" />

        <link rel="canonical" href="https://vedantgo.in" />
      </Helmet>
      <Navbar />

      {/* HERO SECTION */}
      <section className="hero" id="home">
        <motion.div className="hero-bg" style={{ y: y1 }}>
          <img
            src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=2070&auto=format&fit=crop"
            alt="Luxury Car Journey"
          />
        </motion.div>
        <div className="hero-overlay"></div>

        <div className="container relative z-10">
          <motion.div
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            style={{ opacity: opacity1 }}
          >
            <motion.span variants={fadeUp} className="hero-subtitle">Premium Chauffeur Services</motion.span>
            <motion.h1 variants={fadeUp} className="hero-title">
              Exquisite <i className="serif">Journeys</i> Await You.
            </motion.h1>
            <motion.p variants={fadeUp} className="hero-desc">
              Redefining exploration with luxury chauffeur-driven journeys and premium rentals across Maharashtra. Experience comfort, punctuality, and pure elegance.
            </motion.p>
            <motion.div variants={fadeUp} className="hero-actions">
              <button className="btn-primary" onClick={() => setShowInstantBooking(!showInstantBooking)}>
                {showInstantBooking ? 'Close Booking' : 'Instant Booking'}
              </button>
              <a href="https://wa.me/917977584016" target="_blank" rel="noreferrer" className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <MessageCircle size={18} /> Contact Owner Directly
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* COLLAPSABLE INSTANT BOOKING FORM */}
      <AnimatePresence>
        {showInstantBooking && (
          <div className="container relative" style={{ zIndex: 20 }}>
            <motion.form
              className="booking-form-inline"
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0, overflow: 'hidden' }}
              transition={{ duration: 0.5 }}
              onSubmit={(e) => sendToWhatsApp(e, 'Instant Booking')}
            >
              <div className="form-grid">

                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="Name"
                    className="form-input"
                    placeholder="Your Full Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Pick-up Location</label>
                  <input type="text" name="Pick-up" className="form-input" placeholder="City or Area" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Drop-off Location</label>
                  <input type="text" name="Drop-off" className="form-input" placeholder="Destination" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Date & Time</label>
                  <input type="datetime-local" name="Date and Time" className="form-input" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input type="tel" name="Phone" className="form-input" placeholder="+91 00000 00000" required />
                </div>
                <button type="submit" className="btn-primary" style={{ padding: '0.875rem 2rem', height: '100%' }}>
                  Book Now via WhatsApp
                </button>
              </div>
            </motion.form>
          </div>
        )}
      </AnimatePresence>

      {/* SERVICES SECTION WITH IMAGES (Grid 2x2) */}
      <section className="section bg-surface" id="services">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">What We Offer</span>
            <h2 className="section-title serif">Our Premium Services</h2>
          </div>

          <motion.div
            className="services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {servicesData.map((service, idx) => (
              <motion.div key={idx} variants={fadeUp} className="service-image-card" onClick={() => setActiveModal(service.id)}>
                <img src={service.img} alt={service.title} className="service-image-bg" />
                <div className="service-image-overlay"></div>
                <div className="service-image-content">
                  <h3 className="service-image-title serif">{service.title}</h3>
                  <p className="service-image-desc">{service.desc}</p>
                  <button className="btn-outline" style={{ padding: '0.5rem 1.5rem', fontSize: '0.75rem', borderColor: 'var(--accent-gold)', color: 'var(--accent-gold)' }}>
                    Book Now
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CAR RENTAL SECTION */}
      <section className="section" id="rentals">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Self-Drive & Chauffeur</span>
            <h2 className="section-title serif">Premium Car Rentals</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '4rem', alignItems: 'start' }}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h3 className="serif" style={{ fontSize: '2.5rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Drive Your Own Journey</h3>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '2.5rem', fontSize: '1.1rem' }}>
                Whether you need a luxury vehicle for a weekend getaway or a reliable car for an extended business trip, our flexible rental plans cater to your specific needs. Choose between self-drive options or hire one of our expert chauffeurs.
              </p>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', color: 'var(--text-secondary)', marginBottom: '2.5rem' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}><Star size={20} color="var(--accent-gold)" /> Pristine Condition Vehicles</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}><Shield size={20} color="var(--accent-gold)" /> Comprehensive Insurance Included</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}><Phone size={20} color="var(--accent-gold)" /> 24/7 Roadside Assistance</li>
              </ul>
              <button className="btn-primary" onClick={() => setActiveModal('rental_inquiry')}>
                Open Rental Inquiry Form
              </button>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="fleet-image" style={{ marginTop: 0 }}>
              <img src="https://ackodrive-prod.ackoassets.com/image/maruti-suzuki/ertiga/metallic-magma-gray/default/Transparent.png" alt="Premium Car Rental" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FLEET SECTION */}
      <section className="section bg-surface" id="fleet">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Our Signature Fleet</span>
            <h2 className="section-title serif">Travel in Style</h2>
          </div>

          <div className="fleet-grid">
            <motion.div className="fleet-item" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} whileHover={{ scale: 1.02 }}>
              <div className="fleet-info">
                <h3 className="serif">Maruti Suzuki <i>Ertiga</i></h3>
                <p className="fleet-desc">
                  The perfect blend of comfort and efficiency. Our flagship 7-seater luxury vehicle offers spacious interiors, premium amenities, and a phenomenally smooth ride for group travels or business trips.
                </p>
                <div className="fleet-specs">
                  {[
                    { icon: <Users size={18} />, text: "7-Seater Capacity" },
                    { icon: <Zap size={18} />, text: "Smart Hybrid Tech" },
                    { icon: <Wind size={18} />, text: "Advanced AC Cabin" },
                    { icon: <Shield size={18} />, text: "Expert Chauffeur" }
                  ].map((spec, i) => (
                    <div key={i} className="spec-item">
                      <div className="spec-icon">{spec.icon}</div>
                      <span className="spec-text">{spec.text}</span>
                    </div>
                  ))}
                </div>
                <button className="btn-outline" onClick={() => setActiveModal('rental_ertiga')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Reserve Now <ChevronRight size={16} />
                </button>
              </div>
              <div className="fleet-image">
                <img src="https://ackodrive-prod.ackoassets.com/image/maruti-suzuki/ertiga/metallic-magma-gray/default/Transparent.png" alt="Maruti Suzuki Ertiga" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="section" id="reviews">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Client Feedback</span>
            <h2 className="section-title serif">What Our Travelers Say</h2>
          </div>
          <div className="features-grid">
            {testimonials.map((review, idx) => (
              <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} whileHover={{ scale: 1.03, y: -5 }} className="feature-card" style={{ padding: '2rem', textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '1rem', color: 'var(--accent-gold)' }}>
                  {[...Array(review.rating)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="feature-desc" style={{ fontStyle: 'italic', marginBottom: '1.5rem', flexGrow: 1 }}>"{review.text}"</p>
                <h4 className="feature-title" style={{ fontSize: '1rem', marginBottom: 0 }}>{review.name}</h4>
              </motion.div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '4rem' }}>
            <button className="btn-primary" onClick={() => setActiveModal('review_submit')}>Leave a Review</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer" id="contact">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3>VEDANT<span>.</span></h3>
              <p>Redefining exploration with luxury chauffeur-driven journeys across Maharashtra. Experience the pinnacle of travel.</p>
            </div>

            <div>
              <h4 className="footer-title">Explore</h4>
              <div className="footer-links">
                <a href="#home">Home</a>
                <a href="#services">Services</a>
                <a href="#rentals">Rentals</a>
                <a href="#reviews">Reviews</a>
              </div>
            </div>

            <div>
              <h4 className="footer-title">Contact Us</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <Mail size={16} />
                  <span>vedantmhatre2029@gmail.com</span>
                </div>
                <div className="contact-item" style={{ color: 'var(--text-primary)' }}>
                  <Phone size={16} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <span style={{ fontWeight: 'bold' }}>+91 7977584016</span>
                    <span style={{ fontWeight: 'bold' }}>+91 8600798661</span>
                  </div>
                </div>
                <div className="contact-item" style={{ alignItems: 'flex-start' }}>
                  <MapPin size={16} style={{ marginTop: '0.2rem' }} />
                  <span style={{ lineHeight: '1.5' }}>
                    Vedhi Virathan(Budruk), Palghar 401102,<br />
                    Saphale (West), Maharashtra
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 VEDANT TRAVELS. CRAFTED FOR THE EXTRAORDINARY.</p>
            <div className="footer-bottom-links">
              <a href="#privacy" onClick={(e) => { e.preventDefault(); setActiveModal('privacy'); }}>Privacy Policy</a>
              <a href="https://wa.me/917977584016" target="_blank" rel="noreferrer">WhatsApp Support</a>
              <a href="#reviews">Reviews</a>
            </div>
          </div>
        </div>
      </footer>

      {/* MODALS */}
      <AnimatePresence>
        {activeModal && (
          <div className="modal-overlay" onClick={() => setActiveModal(null)}>
            <motion.div
              className="modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className="serif">
                  {activeModal === 'airport' && 'Airport Transfer'}
                  {activeModal === 'railway' && 'Railway Transfer'}
                  {activeModal === 'sightseeing' && 'Local Sightseeing'}
                  {activeModal === 'business' && 'Business Meeting'}
                  {activeModal === 'rental_ertiga' && 'Book Ertiga'}
                  {activeModal === 'rental_inquiry' && 'Rental Inquiry'}
                  {activeModal === 'review_submit' && 'Submit a Review'}
                  {activeModal === 'privacy' && 'Privacy Policy'}
                </h3>
                <button className="modal-close" onClick={() => setActiveModal(null)}><X size={24} /></button>
              </div>
              <div className="modal-body">
                {activeModal === 'privacy' ? (
                  <div style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>1. Introduction</h4>
                    <p style={{ marginBottom: '1.5rem' }}>Welcome to Vedant Travels. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>

                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>2. Information Collection</h4>
                    <p style={{ marginBottom: '1.5rem' }}>We may collect personal information such as your name, phone number, and location details when you submit an inquiry or booking form via WhatsApp.</p>

                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>3. Use of Information</h4>
                    <p style={{ marginBottom: '1.5rem' }}>We use the collected information primarily to process your bookings, communicate with you, and ensure a seamless travel experience.</p>

                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>4. Contact Us</h4>
                    <p>If you have any questions about this Privacy Policy, please contact us directly via WhatsApp at +91 7977584016.</p>
                  </div>
                ) : (
                  <form onSubmit={(e) => sendToWhatsApp(e, activeModal === 'review_submit' ? 'New Customer Review' : `${activeModal} booking`)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                    {activeModal === 'airport' && (
                      <>
                      <div className="form-group">
                        <label className="form-label">Full Name</label>
                        <input
                          type="text"
                          name="Name"
                          className="form-input"
                          placeholder="Your Full Name"
                          required
                        />
                      </div>
                        <div className="form-group">
                          <label className="form-label">Select Airport & Terminal</label>
                          <select name="Airport" className="form-select" required>
                            <option value="">Choose an airport...</option>
                            <option value="Mumbai Airport (T2 - International)">Mumbai Airport (T2 - International)</option>
                            <option value="Mumbai Airport (T1 - Domestic)">Mumbai Airport (T1 - Domestic)</option>
                            <option value="Navi Mumbai International Airport">Navi Mumbai International Airport (Upcoming)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Pick-up / Drop-off Address</label>
                          <input type="text" name="Address" className="form-input" placeholder="Full address" required />
                        </div>
                      </>
                    )}

                    {activeModal === 'railway' && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            name="Name"
                            className="form-input"
                            placeholder="Your Full Name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Select Station</label>
                          <select name="Station" className="form-select" required>
                            <option value="">Choose a station...</option>
                            <option value="CSMT (Mumbai CST)">CSMT (Mumbai CST)</option>
                            <option value="Mumbai Central">Mumbai Central</option>
                            <option value="LTT (Lokmanya Tilak Terminus)">LTT (Lokmanya Tilak Terminus)</option>
                            <option value="Bandra Terminus">Bandra Terminus</option>
                            <option value="Dadar">Dadar</option>
                            <option value="Borivali">Borivali</option>
                            <option value="Thane">Thane</option>
                            <option value="Kalyan">Kalyan</option>
                            <option value="Panvel">Panvel</option>
                            <option value="Vashi">Vashi</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Pick-up / Drop-off Address</label>
                          <input type="text" name="Address" className="form-input" placeholder="Full address" required />
                        </div>
                      </>
                    )}

                    {activeModal === 'sightseeing' && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            name="Name"
                            className="form-input"
                            placeholder="Your Full Name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Select District / Region</label>
                          <select name="District" className="form-select" required>
                            <option value="">Choose a region...</option>
                            <option value="Mumbai (Gateway, Marine Drive, etc)">Mumbai (Gateway, Marine Drive, etc)</option>
                            <option value="Pune (Lonavala, Khandala, City)">Pune (Lonavala, Khandala, City)</option>
                            <option value="Nashik (Vineyards, Trimbakeshwar)">Nashik (Vineyards, Trimbakeshwar)</option>
                            <option value="Raigad (Alibaug, Murud, Forts)">Raigad (Alibaug, Murud, Forts)</option>
                            <option value="Ratnagiri (Ganpatipule, Beaches)">Ratnagiri (Ganpatipule, Beaches)</option>
                            <option value="Sindhudurg (Malvan, Tarkarli)">Sindhudurg (Malvan, Tarkarli)</option>
                            <option value="Aurangabad (Ajanta, Ellora)">Aurangabad (Ajanta, Ellora)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Number of Days</label>
                          <input type="number" name="Days" className="form-input" min="1" placeholder="e.g. 2" required />
                        </div>
                      </>
                    )}

                    {activeModal === 'business' && (
                      <>

                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            name="Name"
                            className="form-input"
                            placeholder="Your Full Name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Pick-up Location</label>
                          <input type="text" name="Pick-up" className="form-input" placeholder="Hotel or Office" required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Meeting Destination</label>
                          <input type="text" name="Destination" className="form-input" placeholder="Office Address" required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Hours Required</label>
                          <select name="Duration" className="form-select" required>
                            <option value="4 Hours / 40 KMs">4 Hours / 40 KMs</option>
                            <option value="8 Hours / 80 KMs">8 Hours / 80 KMs</option>
                            <option value="12 Hours / 120 KMs">12 Hours / 120 KMs</option>
                            <option value="Full Day (Outstation)">Full Day (Outstation)</option>
                          </select>
                        </div>
                      </>
                    )}

                    {activeModal === 'rental_ertiga' && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            name="Name"
                            className="form-input"
                            placeholder="Your Full Name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Vehicle</label>
                          <input type="text" name="Vehicle" className="form-input" value="Maruti Suzuki Ertiga" readOnly />
                        </div>
                        <div className="date-grid">
                          <div className="form-group">
                            <label className="form-label">Start Date</label>
                            <input type="date" name="Start Date" className="form-input" required />
                          </div>
                          <div className="form-group">
                            <label className="form-label">End Date</label>
                            <input type="date" name="End Date" className="form-input" required />
                          </div>
                        </div>
                      </>
                    )}

                    {activeModal === 'rental_inquiry' && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Select Vehicle</label>
                          <select name="Vehicle" className="form-select" required>
                            <option value="">Choose a car...</option>
                            <option value="Maruti Suzuki Ertiga">Maruti Suzuki Ertiga (7-Seater)</option>
                            <option value="Premium Sedan">Premium Sedan</option>
                            <option value="Luxury SUV">Luxury SUV</option>
                          </select>
                        </div>
                        <div className="date-grid">
                          <div className="form-group">
                            <label className="form-label">Start Date</label>
                            <input type="date" name="Start Date" className="form-input" required />
                          </div>
                          <div className="form-group">
                            <label className="form-label">End Date</label>
                            <input type="date" name="End Date" className="form-input" required />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <input type="text" name="Name" className="form-input" placeholder="Your Name" required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Phone Number</label>
                          <input type="tel" name="Phone" className="form-input" placeholder="+91 00000 00000" required />
                        </div>
                      </>
                    )}

                    {activeModal === 'review_submit' && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Your Name</label>
                          <input type="text" name="Name" className="form-input" placeholder="Full Name" required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Rating (Out of 5)</label>
                          <select name="Rating" className="form-select" required>
                            <option value="5">5 - Excellent</option>
                            <option value="4">4 - Good</option>
                            <option value="3">3 - Average</option>
                            <option value="2">2 - Below Average</option>
                            <option value="1">1 - Poor</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Review Comment</label>
                          <textarea name="Review" className="form-input" placeholder="Tell us about your experience..." rows="4" required></textarea>
                        </div>
                      </>
                    )}

                    {/* Common fields for all except rental_ertiga, rental_inquiry, reviews which don't need date/time passenger info */}
                    {(activeModal !== 'rental_ertiga' && activeModal !== 'rental_inquiry' && activeModal !== 'review_submit') && (
                      <div className="form-group">
                        <label className="form-label">Date & Time</label>
                        <input type="datetime-local" name="Date and Time" className="form-input" required />
                      </div>
                    )}

                    {(activeModal !== 'review_submit' && activeModal !== 'rental_inquiry') && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Number of Passengers</label>
                          <input type="number" name="Passengers" className="form-input" min="1" max="7" defaultValue="1" required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Phone Number</label>
                          <input type="tel" name="Phone" className="form-input" placeholder="+91 00000 00000" required />
                        </div>
                      </>
                    )}

                    <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>
                      Confirm via WhatsApp
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default App;